import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { map, catchError, switchMap, filter, delay } from 'rxjs/operators';
import { RequestsRepository } from '../../infrastructure/http/repositories/requests.repository';
import { LoggingService } from '../../core/services/logging.service';
import { FileUploadService } from '../../core/services/file-upload.service';
import {
  RequestsPageActions,
  RequestsAPIActions,
  RequestDetailPageActions,
  RequestDetailAPIActions,
  CreateRequestWizardActions,
} from './requests.actions';
import {
  selectCurrentFilter,
  selectFilterOptions,
  selectRequestStatuses,
} from './requests.selectors';

@Injectable()
export class RequestsEffects {
  private readonly actions$ = inject(Actions);
  private readonly store = inject(Store);
  private readonly repository = inject(RequestsRepository);
  private readonly logger = inject(LoggingService);
  private readonly fileUploadService = inject(FileUploadService);

  loadRequests$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        RequestsPageActions.loadRequests,
        RequestsPageActions.search,
        RequestsPageActions.sort,
        RequestsPageActions.changePage,
        RequestsPageActions.changePageSize,
        RequestsPageActions.applyFilters,
        RequestsPageActions.clearFilters,
        RequestsPageActions.refresh
      ),
      concatLatestFrom(() => this.store.select(selectCurrentFilter)),
      switchMap(([, currentFilter]) => {
        this.logger.debug('Loading requests', { filter: currentFilter });
        return this.repository.getRequests(currentFilter).pipe(
          map(result => {
            this.logger.debug('Requests loaded', {
              totalCount: result.totalCount,
              pageNumber: result.pageNumber,
            });
            return RequestsAPIActions.loadRequestsSuccess({ result });
          }),
          catchError(error => {
            this.logger.error('Failed to load requests', error);
            return of(
              RequestsAPIActions.loadRequestsFailure({
                error: 'Failed to load requests. Please try again.',
              })
            );
          })
        );
      })
    )
  );

  loadFilterOptions$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RequestsAPIActions.loadFilterOptions),
      concatLatestFrom(() => this.store.select(selectFilterOptions)),
      filter(([, existingOptions]) => existingOptions === null),
      switchMap(() => {
        this.logger.debug('Loading filter options');
        return this.repository.getFilterOptions().pipe(
          map(options => {
            this.logger.debug('Filter options loaded', {
              countries: options.countries.length,
              printSites: options.printSites.length,
            });
            return RequestsAPIActions.loadFilterOptionsSuccess({ options });
          }),
          catchError(error => {
            this.logger.error('Failed to load filter options', error);
            return of(
              RequestsAPIActions.loadFilterOptionsFailure({
                error: 'Failed to load filter options.',
              })
            );
          })
        );
      })
    )
  );

  loadRequestDetail$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RequestDetailPageActions.loadRequestDetail),
      switchMap(({ requestNumber }) => {
        this.logger.debug('Loading request detail', { requestNumber });
        return this.repository.getRequestDetail(requestNumber).pipe(
          map(request => {
            if (request) {
              this.logger.debug('Request detail loaded', { requestNumber });
              return RequestDetailAPIActions.loadRequestDetailSuccess({
                request,
              });
            } else {
              this.logger.warn('Request not found', { requestNumber });
              return RequestDetailAPIActions.loadRequestDetailNotFound({
                requestNumber,
              });
            }
          }),
          catchError(error => {
            this.logger.error('Failed to load request detail', error);
            return of(
              RequestDetailAPIActions.loadRequestDetailFailure({
                error: 'Failed to load request details. Please try again.',
              })
            );
          })
        );
      })
    )
  );

  updateRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RequestDetailPageActions.updateRequest),
      switchMap(({ requestNumber, updates, modifiedBy }) => {
        this.logger.debug('Updating request', { requestNumber, updates });
        return this.repository
          .updateRequest(requestNumber, updates, modifiedBy)
          .pipe(
            map(() => {
              this.logger.debug('Request updated successfully', {
                requestNumber,
              });
              return RequestDetailAPIActions.updateRequestSuccess({
                requestNumber,
              });
            }),
            catchError(error => {
              this.logger.error('Failed to update request', error);
              return of(
                RequestDetailAPIActions.updateRequestFailure({
                  error: 'Failed to update request. Please try again.',
                })
              );
            })
          );
      })
    )
  );

  updateRequestStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RequestDetailPageActions.updateRequestStatus),
      concatLatestFrom(() => this.store.select(selectRequestStatuses)),
      switchMap(
        ([{ requestNumber, statusId, modifiedBy }, requestStatuses]) => {
          this.logger.debug('Updating request status', {
            requestNumber,
            statusId,
            hasStatuses: !!requestStatuses,
          });

          // Find the status name from the statuses list
          const selectedStatus = requestStatuses?.find(s => s.id === statusId);
          const statusName = selectedStatus?.status ?? `Status ${statusId}`;

          this.logger.debug('Status name resolved', {
            statusId,
            statusName,
            statusFound: !!selectedStatus,
          });

          return this.repository
            .updateRequestStatus(
              requestNumber,
              statusId,
              modifiedBy,
              statusName
            )
            .pipe(
              map(() => {
                this.logger.debug('Request status updated successfully', {
                  requestNumber,
                });
                return RequestDetailAPIActions.updateRequestStatusSuccess({
                  requestNumber,
                });
              }),
              catchError(error => {
                this.logger.error('Failed to update request status', error);
                return of(
                  RequestDetailAPIActions.updateRequestStatusFailure({
                    error: 'Failed to update request status. Please try again.',
                  })
                );
              })
            );
        }
      )
    )
  );

  assignRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RequestDetailPageActions.assignRequest),
      switchMap(
        ({ requestNumber, assignedUserId, assignedByUserId, modifiedBy }) => {
          this.logger.debug('Assigning request', {
            requestNumber,
            assignedUserId,
          });
          return this.repository
            .assignRequest(
              requestNumber,
              assignedUserId,
              assignedByUserId,
              modifiedBy
            )
            .pipe(
              map(() => {
                this.logger.debug('Request assigned successfully', {
                  requestNumber,
                });
                return RequestDetailAPIActions.assignRequestSuccess({
                  requestNumber,
                });
              }),
              catchError(error => {
                this.logger.error('Failed to assign request', error);
                return of(
                  RequestDetailAPIActions.assignRequestFailure({
                    error: 'Failed to assign request. Please try again.',
                  })
                );
              })
            );
        }
      )
    )
  );

  processRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RequestDetailPageActions.processRequest),
      switchMap(({ requestNumber, request }) => {
        this.logger.debug('Processing request for Uluro', { requestNumber });
        return this.repository
          .processRequestForUluro(requestNumber, request)
          .pipe(
            map(result => {
              this.logger.debug('Request processed successfully', {
                requestNumber,
                transactionsCreated: result.transactionsCreated,
                isOutsourcing: result.isOutsourcing,
              });
              return RequestDetailAPIActions.processRequestSuccess({
                requestNumber,
                result,
              });
            }),
            catchError(error => {
              this.logger.error('Failed to process request', error);
              return of(
                RequestDetailAPIActions.processRequestFailure({
                  error:
                    error.message ??
                    'Failed to process request. Please try again.',
                })
              );
            })
          );
      })
    )
  );

  autoDismissProcessRequestError$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RequestDetailAPIActions.processRequestFailure),
      delay(5000),
      map(() => RequestDetailPageActions.clearProcessRequestError())
    )
  );

  loadPrintLocations$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RequestDetailPageActions.loadPrintLocations),
      switchMap(() => {
        this.logger.debug('Loading print locations');
        return this.repository.getPrintLocations().pipe(
          map(printLocations => {
            this.logger.debug('Print locations loaded', {
              count: printLocations.length,
            });
            return RequestDetailAPIActions.loadPrintLocationsSuccess({
              printLocations,
            });
          }),
          catchError(error => {
            this.logger.error('Failed to load print locations', error);
            return of(
              RequestDetailAPIActions.loadPrintLocationsFailure({
                error: 'Failed to load print locations.',
              })
            );
          })
        );
      })
    )
  );

  loadPrintLocationsBySite$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RequestDetailPageActions.loadPrintLocationsBySite),
      switchMap(({ printSiteId }) => {
        this.logger.debug('Loading print locations by site', { printSiteId });
        return this.repository.getPrintLocationsByPrintSite(printSiteId).pipe(
          map(printLocations => {
            this.logger.debug('Print locations by site loaded', {
              count: printLocations.length,
              printSiteId,
            });
            return RequestDetailAPIActions.loadPrintLocationsBySiteSuccess({
              printLocations,
            });
          }),
          catchError(error => {
            this.logger.error('Failed to load print locations by site', error);
            return of(
              RequestDetailAPIActions.loadPrintLocationsBySiteFailure({
                error: 'Failed to load print locations by site.',
              })
            );
          })
        );
      })
    )
  );

  loadUserList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RequestDetailPageActions.loadUserList),
      switchMap(() => {
        this.logger.debug('Loading user list');
        return this.repository.getUserList().pipe(
          map(userList => {
            this.logger.debug('User list loaded', {
              count: userList.length,
            });
            return RequestDetailAPIActions.loadUserListSuccess({
              userList,
            });
          }),
          catchError(error => {
            this.logger.error('Failed to load user list', error);
            return of(
              RequestDetailAPIActions.loadUserListFailure({
                error: 'Failed to load user list.',
              })
            );
          })
        );
      })
    )
  );

  loadRequestStatuses$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RequestDetailPageActions.loadRequestStatuses),
      switchMap(() => {
        this.logger.debug('Loading request statuses');
        return this.repository.getRequestStatuses().pipe(
          map(requestStatuses => {
            this.logger.debug('Request statuses loaded', {
              count: requestStatuses.length,
            });
            return RequestDetailAPIActions.loadRequestStatusesSuccess({
              requestStatuses,
            });
          }),
          catchError(error => {
            this.logger.error('Failed to load request statuses', error);
            return of(
              RequestDetailAPIActions.loadRequestStatusesFailure({
                error: 'Failed to load request statuses.',
              })
            );
          })
        );
      })
    )
  );

  loadPrintSites$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RequestDetailPageActions.loadPrintSites),
      switchMap(() => {
        this.logger.debug('Loading print sites');
        return this.repository.getPrintSites().pipe(
          map(printSites => {
            this.logger.debug('Print sites loaded', {
              count: printSites.length,
            });
            return RequestDetailAPIActions.loadPrintSitesSuccess({
              printSites,
            });
          }),
          catchError(error => {
            this.logger.error('Failed to load print sites', error);
            return of(
              RequestDetailAPIActions.loadPrintSitesFailure({
                error: 'Failed to load print sites.',
              })
            );
          })
        );
      })
    )
  );

  // Note: updateRequestSuccess and processRequestSuccess intentionally do NOT
  // trigger a reload here. The request-details component orchestrates the
  // update -> process -> reload sequence explicitly so the GET always runs
  // after both calls have settled, instead of racing with either of them.
  reloadRequestAfterUpdate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        RequestDetailAPIActions.updateRequestStatusSuccess,
        RequestDetailAPIActions.assignRequestSuccess
      ),
      map(({ requestNumber }) => {
        this.logger.debug('Reloading request after update', { requestNumber });
        return RequestDetailPageActions.loadRequestDetail({ requestNumber });
      })
    )
  );

  bulkUpdateRequests$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RequestsPageActions.bulkUpdateRequests),
      switchMap(({ payload }) => {
        this.logger.debug('Bulk updating requests', {
          count: payload.requestUpdates.length,
        });
        return this.repository.bulkUpdateRequests(payload).pipe(
          map(() => {
            this.logger.debug('Bulk update successful');
            return RequestsAPIActions.bulkUpdateRequestsSuccess();
          }),
          catchError(error => {
            this.logger.error('Bulk update failed', error);
            return of(
              RequestsAPIActions.bulkUpdateRequestsFailure({
                error: 'Failed to update requests. Please try again.',
              })
            );
          })
        );
      })
    )
  );

  reloadAfterBulkUpdate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RequestsAPIActions.bulkUpdateRequestsSuccess),
      map(() => RequestsPageActions.refresh())
    )
  );

  createRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CreateRequestWizardActions.submitRequest),
      switchMap(action => {
        // Files are already in Swift temp storage — pass their paths in the metadata
        const parsedFiles = this.fileUploadService.parsedFiles.filter(
          f => f.isValid
        );

        const filesList = parsedFiles.map(pf => ({
          fileName: pf.fileName,
          originalFileName: pf.originalFileName,
          totalChecks: pf.checkCount,
          totalAmount: pf.totalAmount,
          totalDDAs: pf.ddaCount,
          totalDocuments: pf.docsInFile,
          countryCode: pf.countryCode,
          uploadedPath: pf.swiftPath ?? '',
        }));

        const metadata = {
          arNumber: action.arNumber,
          userEmail: action.userEmail,
          contactName: action.contactName,
          contactEmails: action.contactEmails,
          contactPhone: action.contactPhone,
          sla: action.isExpedited,
          isSlaFeeWaived: action.isSlaFeeWaived,
          isManualCheck: false,
          isGarnCorp: false,
          isSpecialHandling: false,
          isVisibleOnGrid: true,
          requestGuid: action.requestGuid,
          filesList,
        };

        this.logger.debug('CreateRequest effect: submitting', {
          arNumber: action.arNumber,
          fileCount: parsedFiles.length,
        });

        return this.repository.saveRequest(JSON.stringify(metadata)).pipe(
          map(result => {
            this.logger.debug('CreateRequest effect: success', {
              requestId: result.requestId,
            });
            this.fileUploadService.cancelSession();
            return CreateRequestWizardActions.submitSuccess({
              requestId: result.requestId,
            });
          }),
          catchError(error => {
            this.logger.error('CreateRequest effect: failed', error);
            const apiMessage = (error as { error?: { message?: string } })
              ?.error?.message;
            const friendlyError =
              apiMessage ??
              'Something went wrong while submitting your request. Please try again.';
            return of(
              CreateRequestWizardActions.submitFailure({
                error: friendlyError,
              })
            );
          })
        );
      })
    )
  );
}
